import moment from 'moment';
import { FILTER_CONDITIONS } from '@bringg/types';
import { NamePath } from 'antd/lib/form/interface';
import type { CheckedStrategy } from 'rc-tree-select/lib/utils/strategyUtil';
import { Translations } from './filter-bar-2';

export enum FilterBar2FilterType {
	SEARCH = 'search',
	CUSTOM = 'custom',
	CHECKBOX = 'checkbox',
	RADIO = 'radio',
	SELECT = 'select',
	TREE_SELECT = 'tree_select',
	DATE = 'date',
	DATE_RANGE = 'date_range',
	TIME_RANGE = 'time_range',
	TEXT_CONDITION = 'text_condition',
	NUMBER_CONDITION = 'number_condition',
	DROPDOWN_SEARCH = 'dropdown_search',
	TEAMS = 'teams',
	DATE_RANGE_V2 = 'date_range_v2'
}

export type FilterBar2Entry =
	| number
	| number[]
	| string
	| string[]
	| moment.Moment
	| moment.Moment[]
	| FilterBar2ConditionValue;

export interface FilterBar2Values {
	[name: string]: FilterBar2Entry;
}

type TwoValuesTuple<T> = [T, T];

export type MomentRangeValue = TwoValuesTuple<moment.Moment>;
export type KeyRangeValue = TwoValuesTuple<string>;

export interface Ranges {
	[name: string]: MomentRangeValue;
}

export interface PresetKeyRangeValue {
	translatedLabel: string;
}

export interface PresetKeyRanges {
	[keyword: string]: PresetKeyRangeValue;
}

export interface FilterBar2Value {
	label: string;
	value: string | number;
	children?: FilterBar2Value[];
}

export interface RangeValue {
	from: number;
	to: number;
}

export interface FilterBar2ConditionValue {
	condition: FILTER_CONDITIONS;
	value: string | number | RangeValue;
}

export interface BaseFilterBar2ListItem {
	name: string;
	title?: string;
	tooltip?: string;
	loading?: boolean;
	placeholder?: string;
	additional?: boolean;
	values?: FilterBar2Value[] | FilterBar2ConditionValue;
	temporaryOpened?: boolean;
	applyFilterOnValueChange?: boolean;
	enforceItemUpdate?: boolean;
	disabled?: boolean;
	closeable?: boolean;
	customAllAsOption?: {
		label: string;
	};
}

export type FilterBar2RadioListItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.RADIO;
	values?: FilterBar2Value[];
};

export type FilterBar2DateItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.DATE;
	customProps?: {
		format?: string;
		placeholder?: string;
	};
};

export type FilterBar2SearchItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.SEARCH;
};

export type FilterBar2CheckboxItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.CHECKBOX;
	values?: FilterBar2Value[];
	multiselect?: boolean;
	allowSelectAll?: boolean;
	selectAllThreshold?: number;
	disableSelectAll?: boolean;
	translations?: Translations;
	selectAllTooltip?: string;
	selectAllTooltipThreshold?: number;
	customProps?: {
		onSearch?: (value: string, name: NamePath) => Promise<any>;
	};
};

export type FilterBar2CustomItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.CUSTOM;
	getCountOverride?: (value: FilterBar2Entry) => string;
	component?: Function;
};

export type FilterBar2SelectItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.SELECT;
	multiselect?: boolean;
	values?: FilterBar2Value[];
	allowSelectAll?: boolean;
	selectAllTagText?: string;
	selectAllThreshold?: number;
	emptyAsAll?: boolean;
	maxTagCount?: number | 'responsive';
	maxTagPlaceholder?: string;
	customProps?: {
		onSearch?: (value: string, name: NamePath) => Promise<any>;
	};
};

export type FilterBar2TreeSelectItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.TREE_SELECT;
	values?: FilterBar2Value[];
	selectAllTagText?: string;
	showCheckedStrategy?: CheckedStrategy;
};

export type FilterBar2TeamsItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.TEAMS;
	multiselect?: boolean;
	values?: FilterBar2Value[];
};

export type FilterBar2TimeRangeItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.TIME_RANGE;
};

export type FilterBar2TDropdownSearchItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.DROPDOWN_SEARCH;
};

export type FilterBar2NumberConditionItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.NUMBER_CONDITION;
	values?: FilterBar2ConditionValue;
	minValue?: number;
};

export type FilterBar2TextConditionItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.TEXT_CONDITION;
	values?: FilterBar2ConditionValue;
};

export type FilterBar2DateRangeItem = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.DATE_RANGE;
	customProps?: {
		showTime?: { showSecond?: boolean; format?: string; defaultValue?: [moment.Moment, moment.Moment] };
		ranges?: Ranges;
		disabledDate?: (date) => boolean;
		format?: string;
		placeholder?: [string, string];
	};
};

export type FilterBar2DateRangeItemV2 = BaseFilterBar2ListItem & {
	type: FilterBar2FilterType.DATE_RANGE_V2;
	customProps?: {
		showTime?: { showSecond?: boolean; format?: string; defaultValue?: [moment.Moment, moment.Moment] };
		ranges?: PresetKeyRanges;
		disabledDate?: (date) => boolean;
		format?: string;
		placeholder?: [string, string];
	};
};

export type FilterBar2ListItem =
	| FilterBar2CheckboxItem
	| FilterBar2SearchItem
	| FilterBar2CustomItem
	| FilterBar2RadioListItem
	| FilterBar2SelectItem
	| FilterBar2TreeSelectItem
	| FilterBar2DateItem
	| FilterBar2TimeRangeItem
	| FilterBar2DateRangeItem
	| FilterBar2TDropdownSearchItem
	| FilterBar2NumberConditionItem
	| FilterBar2TextConditionItem
	| FilterBar2TeamsItem
	| FilterBar2DateRangeItemV2;

export interface FilterContentProps {
	name: string;
	values?: FilterBar2Value[];
	multiselect?: boolean;
	placeholder?: string;
	onFilterChange: () => void;
}
